/**
 * 문서 서식 디자이너 draft 상태 — 섹션/필드 조작·바인딩·저장.
 *
 * 설계: docs/designs/core-document-template-engine.md §7.6
 * MES HACCP `use-haccp-template-draft.ts` 를 문서 종류 중립으로 일반화한 것이다.
 * 제거: 분류/주기/CCP/센서. 추가: docType·isDefault·**bind(데이터 연결)**·PARTY_BOX/TOTALS.
 */
import { computed, nextTick, reactive, ref, watch, type ComputedRef, type Ref } from 'vue';
import { ElMessage } from 'element-plus';
import type {
  DocColumnDef,
  DocFieldBinding,
  DocValueFormat,
  DocFieldDef,
  DocSectionTable,
  DocSectionTotals,
  DocTemplateLayout,
  DocTemplateSection,
  DocumentFieldCatalogEntry,
  DocumentTypeCatalog,
} from '@axsolutionsdev/shared';

import {
  createDocumentTemplate,
  reviseDocumentTemplate,
  updateDocumentTemplate,
  type DocumentTemplateRow,
} from '../../../api/documents';

export type TableSection = DocSectionTable;
export type TotalsSection = DocSectionTotals;
export type PartySection = Extract<DocTemplateSection, { type: 'PARTY_BOX' }>;
export type ChecklistSection = Extract<DocTemplateSection, { type: 'CHECKLIST' }>;

export const SECTION_TYPE_LABELS: Record<string, string> = {
  HEADER_FIELDS: '머리말 필드',
  PARTY_BOX: '거래 당사자',
  TABLE: '표',
  TOTALS: '합계',
  CHECKLIST: '점검 체크리스트',
  TEXT_BLOCK: '서술란',
  SIGNATURE: '서명란',
};

/**
 * 모든 문서 종류에 공통으로 존재하는 컨텍스트 경로 — 엔진이 주입하므로 공급자
 * 카탈로그에는 없다. 디자이너 드롭다운에서 함께 고를 수 있어야 한다.
 */
export const CORE_CATALOG_ENTRIES: DocumentFieldCatalogEntry[] = [
  { path: 'company.name', label: '(자사) 상호', kind: 'SCALAR' },
  { path: 'company.bizNo', label: '(자사) 사업자등록번호', kind: 'SCALAR' },
  { path: 'company.ceo', label: '(자사) 대표자', kind: 'SCALAR' },
  { path: 'company.address', label: '(자사) 주소', kind: 'SCALAR' },
  { path: 'company.phone', label: '(자사) 전화', kind: 'SCALAR' },
  { path: 'meta.issuedAt', label: '발행일시', kind: 'SCALAR' },
  { path: 'meta.issuedByName', label: '발행자', kind: 'SCALAR' },
  { path: 'meta.templateName', label: '서식명', kind: 'SCALAR' },
];

export interface DocumentTemplateDraftMeta {
  docType: string;
  name: string;
  note: string;
  sortOrder: number;
  isDefault: boolean;
}

export interface DocumentTemplateDraft {
  draftId: Ref<string | null>;
  draftVersion: Ref<number>;
  draftIsActive: Ref<boolean>;
  saving: Ref<boolean>;
  meta: DocumentTemplateDraftMeta;
  layout: DocTemplateLayout;
  catalog: Ref<DocumentTypeCatalog | null>;
  hasChanges: ComputedRef<boolean>;
  titleTouched: Ref<boolean>;
  selectedSectionKey: Ref<string | null>;
  selectedFieldKey: Ref<string | null>;
  selectedSection: ComputedRef<DocTemplateSection | null>;
  selectedField: ComputedRef<DocFieldDef | null>;
  selectedSectionTitle: Ref<string>;
  newKey: (prefix: string) => string;
  sectionFields: (section: DocTemplateSection | null) => DocFieldDef[];
  sectionDisplayName: (section: DocTemplateSection) => string;
  /** 선택 필드에 연결 가능한 카탈로그 경로 (BOUND 표 컬럼이면 그 표의 ROW 경로만) */
  bindingOptions: ComputedRef<DocumentFieldCatalogEntry[]>;
  applyBinding: (field: DocFieldDef, path: string | null) => void;
  setBindingFormat: (bind: DocFieldBinding | undefined, format: string | null) => void;
  openCreate: (docType: string, preset?: DocTemplateLayout | null) => void;
  openEdit: (row: DocumentTemplateRow) => void;
  markSaved: () => void;
  markDirty: () => void;
  addSection: (type: string, afterKey?: string | null) => void;
  removeSection: (key: string) => DocTemplateSection | null;
  restoreSection: (section: DocTemplateSection, index: number) => void;
  addField: (section: DocTemplateSection) => void;
  removeField: (
    section: DocTemplateSection,
    key: string,
  ) => { field: DocFieldDef; index: number } | null;
  restoreField: (section: DocTemplateSection, field: DocFieldDef, index: number) => void;
  setFixedRows: (section: TableSection, labels: string[]) => void;
  addTotalRow: (section: TotalsSection) => void;
  removeTotalRow: (section: TotalsSection, index: number) => void;
  splitCsv: (raw: string) => string[];
  validateDraft: () => { blocking: string[]; warnings: string[] };
  saveDraft: () => Promise<DocumentTemplateRow | null>;
  saveAsRevision: () => Promise<DocumentTemplateRow | null>;
}

function emptyLayout(): DocTemplateLayout {
  return {
    page: {
      size: 'A4',
      orientation: 'portrait',
      title: '',
      showApprovalLine: true,
      approvalStages: 2,
      showCompanyLogo: true,
      showIssuedAt: true,
    },
    sections: [],
  };
}

export function useDocumentTemplateDraft(): DocumentTemplateDraft {
  const draftId = ref<string | null>(null);
  const draftVersion = ref(1);
  const draftIsActive = ref(true);
  const saving = ref(false);
  const catalog = ref<DocumentTypeCatalog | null>(null);
  const meta = reactive<DocumentTemplateDraftMeta>({
    docType: '',
    name: '',
    note: '',
    sortOrder: 0,
    isDefault: false,
  });
  const layout = reactive<DocTemplateLayout>(emptyLayout());
  const selectedSectionKey = ref<string | null>(null);
  const selectedFieldKey = ref<string | null>(null);
  const titleTouched = ref(false);

  // 더티 추적 — 템플릿의 v-model 직접 편집까지 잡아야 하므로 deep watch 1개로 플래그만 세운다
  // (키 입력마다 전체 layout 을 직렬화 비교하는 것보다 싸다). markSaved 는 nextTick 에서
  // 한 번 더 지워 같은 틱의 프로그램적 초기화(openCreate/openEdit)가 남긴 발화를 무시한다.
  const dirty = ref(false);
  watch(
    () => [meta, layout],
    () => {
      dirty.value = true;
    },
    { deep: true },
  );
  const hasChanges = computed(() => dirty.value);

  const selectedSection = computed<DocTemplateSection | null>(
    () => layout.sections.find((s) => s.key === selectedSectionKey.value) ?? null,
  );

  const selectedSectionTitle = computed<string>({
    get: () => {
      const s = selectedSection.value;
      return s && 'title' in s ? (s.title ?? '') : '';
    },
    set: (v: string) => {
      const s = selectedSection.value;
      if (!s || s.type === 'SIGNATURE' || s.type === 'TEXT_BLOCK') return;
      if (v) s.title = v;
      else delete (s as { title?: string }).title;
    },
  });

  function sectionFields(section: DocTemplateSection | null): DocFieldDef[] {
    if (!section) return [];
    if (section.type === 'HEADER_FIELDS') return section.fields;
    if (section.type === 'TABLE') return section.columns;
    return [];
  }

  const selectedField = computed<DocFieldDef | null>(() => {
    const section = selectedSection.value;
    if (!section) return null;
    return sectionFields(section).find((f) => f.key === selectedFieldKey.value) ?? null;
  });

  /**
   * 선택 위치에 맞는 바인딩 후보.
   * - BOUND 표 컬럼: 그 표 `dataKey` 의 ROW 경로만 (다른 표 경로를 고르면 값이 안 나온다)
   * - 그 외: SCALAR 경로 + 코어 공통 경로
   */
  const bindingOptions = computed<DocumentFieldCatalogEntry[]>(() => {
    const fields = catalog.value?.fields ?? [];
    const section = selectedSection.value;
    if (section?.type === 'TABLE' && section.rowMode === 'BOUND') {
      return fields.filter((f) => f.kind === 'ROW' && f.dataKey === section.dataKey);
    }
    return [...fields.filter((f) => f.kind === 'SCALAR'), ...CORE_CATALOG_ENTRIES];
  });

  let keySeq = 1;
  function newKey(prefix: string): string {
    return `${prefix}_${Date.now().toString(36)}_${keySeq++}`;
  }

  function markSaved(): void {
    dirty.value = false;
    void nextTick(() => {
      dirty.value = false;
    });
  }

  /** 프로그램적 초기화 직후에도 "미저장 새 내용" 으로 취급 — markSaved 의 nextTick 을 뒤엎는다. */
  function markDirty(): void {
    void nextTick(() => {
      dirty.value = true;
    });
  }

  function resetTo(next: DocTemplateLayout): void {
    layout.page = next.page;
    layout.sections = next.sections;
  }

  function openCreate(docType: string, preset?: DocTemplateLayout | null): void {
    draftId.value = null;
    draftVersion.value = 1;
    draftIsActive.value = true;
    meta.docType = docType;
    meta.name = '';
    meta.note = '';
    meta.sortOrder = 0;
    meta.isDefault = false;
    if (preset) {
      resetTo(JSON.parse(JSON.stringify(preset)) as DocTemplateLayout);
      titleTouched.value = true;
    } else {
      resetTo(emptyLayout());
      addSection('HEADER_FIELDS');
      addSection('TABLE');
      addSection('SIGNATURE');
      titleTouched.value = false;
    }
    selectedSectionKey.value = layout.sections[0]?.key ?? null;
    selectedFieldKey.value = null;
    markSaved();
  }

  function openEdit(row: DocumentTemplateRow): void {
    draftId.value = row.id;
    draftVersion.value = row.version;
    draftIsActive.value = row.isActive;
    meta.docType = row.docType;
    meta.name = row.name;
    meta.note = row.note ?? '';
    meta.sortOrder = row.sortOrder;
    meta.isDefault = row.isDefault;
    resetTo(JSON.parse(JSON.stringify(row.layout)) as DocTemplateLayout);
    selectedSectionKey.value = layout.sections[0]?.key ?? null;
    selectedFieldKey.value = null;
    titleTouched.value = true; // 기존 서식은 인쇄 제목을 서식명에 자동 추종시키지 않는다
    markSaved();
  }

  // ---------------------------------------------------------------------------
  // 섹션 조작
  // ---------------------------------------------------------------------------

  /** 카탈로그에 표(BOUND) 후보가 있으면 표 기본값을 데이터 연결로 — 업무문서의 기본 형태. */
  function defaultTableSection(): TableSection {
    const firstTable = catalog.value?.tables?.[0];
    if (firstTable) {
      return {
        type: 'TABLE',
        key: newKey('tbl'),
        rowMode: 'BOUND',
        dataKey: firstTable.key,
        showRowNo: true,
        minRows: 5,
        columns: [{ key: newKey('c'), label: '항목', inputType: 'TEXT' }],
      };
    }
    return {
      type: 'TABLE',
      key: newKey('tbl'),
      rowMode: 'FIXED',
      rowHeaderLabel: '시간',
      fixedRows: [{ label: '09:00' }, { label: '13:00' }, { label: '17:00' }],
      columns: [
        { key: newKey('c'), label: '측정값', inputType: 'NUMBER' },
        { key: newKey('c'), label: '확인자', inputType: 'TEXT' },
      ],
    };
  }

  function addSection(type: string, afterKey?: string | null): void {
    let section: DocTemplateSection;
    switch (type) {
      case 'HEADER_FIELDS':
        section = {
          type: 'HEADER_FIELDS',
          key: newKey('head'),
          columnsPerRow: 2,
          fields: [
            { key: newKey('f'), label: '문서번호', inputType: 'TEXT' },
            { key: newKey('f'), label: '일자', inputType: 'DATE' },
          ],
        };
        break;
      case 'PARTY_BOX':
        section = {
          type: 'PARTY_BOX',
          key: newKey('party'),
          parties: [
            { role: 'SUPPLIER', label: '공 급 자', source: 'COMPANY', showSeal: true },
            { role: 'BUYER', label: '공급받는자', source: 'CONTEXT', pathPrefix: 'header.partner' },
          ],
        };
        break;
      case 'TABLE':
        section = defaultTableSection();
        break;
      case 'TOTALS':
        section = {
          type: 'TOTALS',
          key: newKey('tot'),
          align: 'right',
          rows: [
            {
              label: '합계 금액',
              bind: { path: 'totals.totalAmount', format: 'MONEY' },
              emphasis: true,
            },
          ],
        };
        break;
      case 'CHECKLIST':
        section = {
          type: 'CHECKLIST',
          key: newKey('chk'),
          resultType: 'PASS_FAIL',
          items: [{ key: newKey('i'), label: '점검 항목 1' }],
        };
        break;
      case 'TEXT_BLOCK':
        section = { type: 'TEXT_BLOCK', key: newKey('txt'), label: '특이사항', rows: 3 };
        break;
      default:
        section = { type: 'SIGNATURE', key: newKey('sig'), signers: ['WRITER', 'CONFIRMER'] };
    }
    const at = afterKey ? layout.sections.findIndex((s) => s.key === afterKey) : -1;
    if (at >= 0) layout.sections.splice(at + 1, 0, section);
    else layout.sections.push(section);
    selectedSectionKey.value = section.key;
    selectedFieldKey.value = null;
  }

  function removeSection(key: string): DocTemplateSection | null {
    const idx = layout.sections.findIndex((s) => s.key === key);
    if (idx < 0) return null;
    const [removed] = layout.sections.splice(idx, 1);
    if (selectedSectionKey.value === key) {
      selectedSectionKey.value =
        layout.sections[Math.min(idx, layout.sections.length - 1)]?.key ?? null;
      selectedFieldKey.value = null;
    }
    return removed ?? null;
  }

  function restoreSection(section: DocTemplateSection, index: number): void {
    layout.sections.splice(Math.min(index, layout.sections.length), 0, section);
    selectedSectionKey.value = section.key;
  }

  function sectionDisplayName(section: DocTemplateSection): string {
    if ('title' in section && section.title) return section.title;
    if (section.type === 'TEXT_BLOCK') return section.label;
    return '';
  }

  // ---------------------------------------------------------------------------
  // 필드 조작
  // ---------------------------------------------------------------------------

  function addField(section: DocTemplateSection): void {
    const fields = sectionFields(section);
    const field: DocFieldDef = {
      key: newKey(section.type === 'TABLE' ? 'c' : 'f'),
      label: `새 항목 ${fields.length + 1}`,
      inputType: 'TEXT',
    };
    const at = selectedFieldKey.value
      ? fields.findIndex((f) => f.key === selectedFieldKey.value)
      : -1;
    if (at >= 0) fields.splice(at + 1, 0, field);
    else fields.push(field);
    selectedFieldKey.value = field.key;
  }

  function removeField(
    section: DocTemplateSection,
    key: string,
  ): { field: DocFieldDef; index: number } | null {
    const fields = sectionFields(section);
    const idx = fields.findIndex((f) => f.key === key);
    if (idx < 0) return null;
    const [removed] = fields.splice(idx, 1);
    if (!removed) return null;
    if (selectedFieldKey.value === key) {
      selectedFieldKey.value = fields[Math.min(idx, fields.length - 1)]?.key ?? null;
    }
    return { field: removed, index: idx };
  }

  function restoreField(section: DocTemplateSection, field: DocFieldDef, index: number): void {
    const fields = sectionFields(section);
    fields.splice(Math.min(index, fields.length), 0, field);
    selectedFieldKey.value = field.key;
  }

  function setFixedRows(section: TableSection, labels: string[]): void {
    section.fixedRows = labels.map((label) => ({ label }));
  }

  function addTotalRow(section: TotalsSection): void {
    section.rows.push({ label: '항목', bind: { path: '', format: 'MONEY' } });
  }

  function removeTotalRow(section: TotalsSection, index: number): void {
    section.rows.splice(index, 1);
  }

  function splitCsv(raw: string): string[] {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // ---------------------------------------------------------------------------
  // 데이터 연결(bind)
  // ---------------------------------------------------------------------------

  /** 카탈로그에서 고른 경로를 필드에 연결한다. null 이면 연결 해제(=입력형 빈칸). */
  function applyBinding(field: DocFieldDef, path: string | null): void {
    if (!path) {
      delete field.bind;
      return;
    }
    const entry = [...(catalog.value?.fields ?? []), ...CORE_CATALOG_ENTRIES].find(
      (f) => f.path === path,
    );
    const bind: DocFieldBinding = { path };
    if (entry?.suggest) bind.format = entry.suggest;
    field.bind = bind;
    // 권장 포맷이 금액/수량이면 표 컬럼은 우측 정렬이 자연스럽다
    if (entry?.suggest && ['MONEY', 'QTY', 'NUMBER', 'PERCENT'].includes(entry.suggest)) {
      (field as DocColumnDef).align = 'right';
    }
  }

  function setBindingFormat(bind: DocFieldBinding | undefined, format: string | null): void {
    if (!bind) return;
    if (!format) delete bind.format;
    else bind.format = format as DocValueFormat;
  }

  // ---------------------------------------------------------------------------
  // 검증·저장
  // ---------------------------------------------------------------------------

  function buildPayload(): {
    docType: string;
    name: string;
    note: string | null;
    sortOrder: number;
    layout: DocTemplateLayout;
  } {
    const page = { ...layout.page, title: layout.page.title.trim() || meta.name.trim() };
    if (!page.subtitle?.trim()) delete page.subtitle;
    return {
      docType: meta.docType,
      name: meta.name.trim(),
      note: meta.note.trim() || null,
      sortOrder: meta.sortOrder,
      // reactive 참조 유출 방지 — 저장 시점 스냅샷
      layout: JSON.parse(JSON.stringify({ page, sections: layout.sections })) as DocTemplateLayout,
    };
  }

  function validateDraft(): { blocking: string[]; warnings: string[] } {
    const blocking: string[] = [];
    const warnings: string[] = [];
    if (!meta.docType) blocking.push('문서 종류를 선택하세요.');
    if (!meta.name.trim()) blocking.push('서식명을 입력하세요.');
    if (layout.sections.length === 0) blocking.push('섹션을 1개 이상 추가하세요.');

    const known = new Set(
      [...(catalog.value?.fields ?? []), ...CORE_CATALOG_ENTRIES].map((f) => f.path),
    );
    for (const section of layout.sections) {
      const label =
        sectionDisplayName(section) || SECTION_TYPE_LABELS[section.type] || section.type;
      if (section.type === 'TABLE') {
        if (section.columns.length === 0) warnings.push(`[${label}] 컬럼이 없습니다.`);
        if (section.rowMode === 'BOUND' && !section.dataKey) {
          blocking.push(`[${label}] 데이터 연결 표는 연결할 목록을 골라야 합니다.`);
        }
      }
      if (section.type === 'CHECKLIST' && section.items.length === 0) {
        warnings.push(`[${label}] 점검 항목이 없습니다.`);
      }
      if (section.type === 'TOTALS') {
        // 서버 layout 검증이 빈 bind.path 를 거부하므로 경고가 아니라 차단이어야 한다
        // (경고로 두면 저장 버튼이 서버 400 을 그대로 토스트로 뱉는다).
        for (const row of section.rows) {
          if (!row.bind.path) {
            blocking.push(
              `[${label}] "${row.label || '(라벨 없음)'}" 에 데이터 연결을 지정하세요.`,
            );
          }
        }
      }
      for (const f of sectionFields(section)) {
        if (!f.label.trim()) warnings.push(`[${label}] 라벨이 빈 필드가 있습니다.`);
        if (f.bind?.path && !known.has(f.bind.path)) {
          warnings.push(`[${label}] "${f.label}" 의 연결 경로 ${f.bind.path} 는 목록에 없습니다.`);
        }
      }
    }
    return { blocking, warnings };
  }

  async function saveDraft(): Promise<DocumentTemplateRow | null> {
    saving.value = true;
    try {
      const payload = buildPayload();
      const row = draftId.value
        ? await updateDocumentTemplate(draftId.value, {
            name: payload.name,
            layout: payload.layout,
            note: payload.note,
            sortOrder: payload.sortOrder,
          })
        : await createDocumentTemplate({ ...payload, isDefault: meta.isDefault });
      draftId.value = row.id;
      draftVersion.value = row.version;
      meta.isDefault = row.isDefault;
      markSaved();
      ElMessage.success('저장되었습니다.');
      return row;
    } catch (e) {
      ElMessage.error(e instanceof Error ? e.message : '저장 실패');
      return null;
    } finally {
      saving.value = false;
    }
  }

  async function saveAsRevision(): Promise<DocumentTemplateRow | null> {
    if (!draftId.value) return null;
    saving.value = true;
    try {
      const payload = buildPayload();
      const row = await reviseDocumentTemplate(draftId.value, {
        layout: payload.layout,
        name: payload.name,
        note: payload.note,
      });
      draftId.value = row.id;
      draftVersion.value = row.version;
      meta.isDefault = row.isDefault;
      markSaved();
      ElMessage.success(`개정 v${row.version} 이 발행되었습니다.`);
      return row;
    } catch (e) {
      ElMessage.error(e instanceof Error ? e.message : '개정 실패');
      return null;
    } finally {
      saving.value = false;
    }
  }

  return {
    draftId,
    draftVersion,
    draftIsActive,
    saving,
    meta,
    layout,
    catalog,
    hasChanges,
    titleTouched,
    selectedSectionKey,
    selectedFieldKey,
    selectedSection,
    selectedField,
    selectedSectionTitle,
    newKey,
    sectionFields,
    sectionDisplayName,
    bindingOptions,
    applyBinding,
    setBindingFormat,
    openCreate,
    openEdit,
    markSaved,
    markDirty,
    addSection,
    removeSection,
    restoreSection,
    addField,
    removeField,
    restoreField,
    setFixedRows,
    addTotalRow,
    removeTotalRow,
    splitCsv,
    validateDraft,
    saveDraft,
    saveAsRevision,
  };
}
